import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Users, DollarSign, TrendingUp, Activity, ChevronRight, ChevronLeft,
  Search, X, RefreshCw, ArrowUpRight, ArrowDownRight, Minus, ArrowLeft,
  Info, AlertTriangle,
} from "lucide-react";

/**
 * Intuiterview Owner Analytics - Phase 1.
 *
 * Single-page tabbed dashboard: Overview / Users / User Detail / Funnel.
 * All numbers come from `/api/admin/analytics/*` which reads real
 * production collections. No hardcoded values, no mocks. Access is gated
 * both here (frontend hiding for UX) and by `role == "admin"` on every
 * backend endpoint (server-side, cannot be bypassed).
 */

const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 Days" },
  { id: "30d", label: "30 Days" },
  { id: "90d", label: "90 Days" },
  { id: "all", label: "All Time" },
];

function EnvironmentBadge() {
  const host = window.location.hostname;
  const isProd = !/preview\.emergentagent\.com$|^localhost$|^127\./.test(host);
  return (
    <div className="text-xs mt-1 flex items-center gap-1.5" data-testid="env-badge">
      <span
        className={`text-[9px] uppercase tracking-widest font-semibold rounded-full px-1.5 py-0.5 border ${
          isProd
            ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
            : "text-amber-300 border-amber-500/40 bg-amber-500/10"
        }`}
      >
        {isProd ? "Production" : "Preview / Test"}
      </span>
      <span className="text-zinc-500">Live data from {host}</span>
    </div>
  );
}

function DataHygienePanel({ onPurged }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/analytics/test-accounts");
      setRows(data.accounts || []);
      const s = {};
      (data.accounts || []).forEach((a) => { s[a.user_id] = true; });
      setSel(s);
    } catch { /* ignore */ } finally { setBusy(false); }
  }, []);

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);

  const purge = async () => {
    setBusy(true);
    setConfirming(false);
    try {
      const { data } = await api.post("/admin/analytics/test-accounts/purge", {
        user_ids: selectedIds, confirm: "DELETE",
      });
      setResult(data);
      await load();
      onPurged?.();
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-4" data-testid="data-hygiene-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400">Data Hygiene</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Review automated test accounts (test_* / qa.demo.* @offerready.app). Real users can never be deleted here, the server re-verifies every account against the test pattern.
          </div>
        </div>
        <button
          onClick={load}
          disabled={busy}
          data-testid="hygiene-review-btn"
          className="text-xs border border-zinc-700 hover:border-zinc-500 text-zinc-200 rounded-lg px-3 py-1.5 disabled:opacity-60"
        >
          {rows === null ? "Review test accounts" : "Refresh"}
        </button>
      </div>
      {result && (
        <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2 mt-3" data-testid="hygiene-result">
          Deleted {result.purged_count} test account{result.purged_count === 1 ? "" : "s"} (and all their data). Audit-logged.
        </div>
      )}
      {rows !== null && (
        <div className="mt-3">
          {rows.length === 0 ? (
            <div className="text-xs text-zinc-500">No test accounts found. Database is clean.</div>
          ) : (
            <>
              <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-lg divide-y divide-zinc-800/70">
                {rows.map((a) => (
                  <label key={a.user_id} className="flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900/60 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!sel[a.user_id]}
                      onChange={(e) => setSel((s) => ({ ...s, [a.user_id]: e.target.checked }))}
                      data-testid={`hygiene-check-${a.user_id}`}
                      className="accent-amber-500"
                    />
                    <span className="font-mono text-zinc-200 flex-1 truncate">{a.email}</span>
                    <span className="text-zinc-500">{(a.registered_at || "").slice(0, 10)}</span>
                    <span className="text-zinc-600">{a.workspaces}ws · {a.resumes}res · {a.interviews}int</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2.5">
                <div className="text-[11px] text-zinc-500">{selectedIds.length} of {rows.length} selected</div>
                {!confirming ? (
                  <button
                    onClick={() => setConfirming(true)}
                    disabled={busy || selectedIds.length === 0}
                    data-testid="hygiene-delete-btn"
                    className="text-xs bg-rose-600/90 hover:bg-rose-500 text-white rounded-lg px-3 py-1.5 disabled:opacity-50"
                  >
                    Delete {selectedIds.length} selected…
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-rose-300">Permanently delete {selectedIds.length} accounts and all their data?</span>
                    <button onClick={purge} disabled={busy} data-testid="hygiene-confirm-btn" className="text-xs bg-rose-600 hover:bg-rose-500 text-white rounded-lg px-3 py-1.5">Yes, delete</button>
                    <button onClick={() => setConfirming(false)} data-testid="hygiene-cancel-btn" className="text-xs text-zinc-400 hover:text-zinc-200 px-2">Cancel</button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function fmtCents(cents) {
  if (typeof cents !== "number" || isNaN(cents)) return "$0";
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
}
function fmtNum(n) { return (n ?? 0).toLocaleString(); }
function fmtDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
  } catch { return "–"; }
}
function fmtDateTime(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch { return "–"; }
}
function ago(iso) {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86_400_000);
  if (d < 1) return "today";
  if (d === 1) return "1d ago";
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}
function pct(a, b) {
  if (!b || b <= 0) return null;
  return Math.round(((a - b) / b) * 100);
}

function DeltaChip({ current, previous, positiveIsGood = true }) {
  if (previous === null || previous === undefined) return null;
  const p = pct(current, previous);
  if (p === null) return null;
  const isUp = p > 0, isDown = p < 0;
  const good = (positiveIsGood && isUp) || (!positiveIsGood && isDown);
  const bad = (positiveIsGood && isDown) || (!positiveIsGood && isUp);
  const color = good ? "text-emerald-400" : bad ? "text-rose-400" : "text-zinc-500";
  const Icon = isUp ? ArrowUpRight : isDown ? ArrowDownRight : Minus;
  return (
    <span data-testid="delta-chip" className={`inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums ${color}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(p)}%
    </span>
  );
}

function KpiCard({ label, value, sub, current, previous, positiveIsGood = true, testid, tooltip }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
        <span>{label}</span>
        {tooltip && <MetricTooltip text={tooltip} testid={`${testid}-info`} />}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-2xl font-semibold text-zinc-50 tabular-nums">{value}</div>
        <DeltaChip current={current} previous={previous} positiveIsGood={positiveIsGood} />
      </div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * MetricTooltip - small ⓘ hover badge that reveals the exact definition of
 * a KPI. Definitions come from the backend `METRIC_DEFINITIONS` so tooltip
 * copy can never drift from the calculation. CSS-only (group-hover) - no
 * portal, no library, no positioning JS. Positioned above the badge so it
 * doesn't get clipped by the card border.
 */
function MetricTooltip({ text, testid }) {
  return (
    <span className="relative inline-flex group" data-testid={testid}>
      <Info className="h-3 w-3 text-zinc-600 group-hover:text-amber-400 cursor-help" />
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-64 rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl px-3 py-2 text-[11px] text-zinc-300 leading-snug opacity-0 group-hover:opacity-100 transition-opacity z-50 normal-case tracking-normal"
      >
        {text}
      </span>
    </span>
  );
}

function TabButton({ id, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`admin-tab-${id}`}
      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
        active
          ? "bg-amber-500/[0.12] border-amber-500/40 text-amber-200"
          : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700"
      }`}
    >
      {label}
    </button>
  );
}

function RangeChips({ range, onChange }) {
  return (
    <div className="inline-flex flex-wrap items-center gap-1.5" data-testid="range-picker">
      {RANGES.map((r) => (
        <button
          key={r.id}
          onClick={() => onChange(r.id)}
          data-testid={`range-${r.id}`}
          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
            range === r.id
              ? "bg-amber-500/[0.12] border-amber-500/40 text-amber-200"
              : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700"
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

// ---------- Overview -------------------------------------------------------

function OverviewTab({ range, setRange }) {
  const [data, setData] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [ov, rd] = await Promise.all([
        api.get(`/admin/analytics/overview?range=${range}`),
        api.get(`/admin/analytics/readiness_impact?range=${range}`),
      ]);
      setData(ov.data);
      setReadiness(rd.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-sm text-zinc-500">Loading overview…</div>;
  if (err) return <div className="text-sm text-rose-400">{err}</div>;
  if (!data) return null;

  const c = data.current || {};
  const p = data.previous;
  const defs = data.definitions || {};
  // Reconciliation note: when there are paid customers but $0 cash, it's
  // because those users were activated via `beta_activation` (no charge).
  // We surface this in the Revenue card so nothing looks broken.
  const hasBetaOnly = (c.paid_activations || 0) > 0 && (c.revenue_cents || 0) === 0;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RangeChips range={range} onChange={setRange} />
        <button
          onClick={load}
          data-testid="overview-refresh"
          className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-amber-300"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="kpi-total-users" label="Total Users" value={fmtNum(c.total_users)} tooltip={defs.total_users} />
        <KpiCard testid="kpi-new-users"   label="New Users"   value={fmtNum(c.new_users)}   current={c.new_users}   previous={p?.new_users}   tooltip={defs.new_users} />
        <KpiCard testid="kpi-active-users" label="Active Users" value={fmtNum(c.active_users)} current={c.active_users} previous={p?.active_users} tooltip={defs.active_users} />
        <KpiCard testid="kpi-free-users"  label="Free Users"  value={fmtNum(c.free_users)}  tooltip={defs.free_users} />
        <KpiCard testid="kpi-paid-users"  label="Paid Customers" value={fmtNum(c.paid_customers)} tooltip={defs.paid_customers} />
        <KpiCard
          testid="kpi-paid-activations"
          label="Paid Activations"
          value={fmtNum(c.paid_activations)}
          sub={hasBetaOnly ? "incl. beta activations (no cash)" : `in last ${range === "all" ? "all time" : range}`}
          tooltip={defs.paid_activations}
        />
        <KpiCard
          testid="kpi-purchases"
          label="Cash Purchases"
          value={fmtNum(c.total_purchases)}
          current={c.total_purchases}
          previous={p?.total_purchases}
          sub="real payments only"
          tooltip={defs.total_purchases}
        />
        <KpiCard
          testid="kpi-revenue"
          label="Revenue"
          value={fmtCents(c.revenue_cents)}
          sub={hasBetaOnly ? "$0, activations were beta grants" : (range === "all" ? "all time" : `in last ${range}`)}
          current={c.revenue_cents}
          previous={p?.revenue_cents}
          tooltip={defs.revenue_cents}
        />
        <KpiCard testid="kpi-mrr" label="MRR" value={fmtCents(c.mrr_cents)} sub="active monthly subscribers" tooltip={defs.mrr_cents} />
      </div>

      {hasBetaOnly && (
        <div data-testid="beta-activation-notice" className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-100/90 leading-relaxed">
            <span className="font-semibold text-amber-200">Data reconciliation:</span>{" "}
            {c.paid_activations} paid activation{c.paid_activations === 1 ? "" : "s"} in this range with $0 cash collected, Stripe isn&apos;t wired yet, so paid customers received their entitlement via beta activation. This is expected and consistent.
          </div>
        </div>
      )}

      <ReadinessImpactPanel data={readiness} />

      <RevenueChart series={data.daily_revenue || []} range={range} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlanBreakdown breakdown={data.plan_breakdown || []} />
        <RefundsLog rows={data.refunds_log || []} range={range} />
      </div>

      {(c.total_users || 0) === 0 && (
        <div className="text-sm text-zinc-500 py-8 text-center">No data yet, invite users to sign up.</div>
      )}
    </div>
  );
}

// ---------- Overview Sub-Components ----------------------------------------

/**
 * ReadinessImpactPanel - Intuiterview's headline product-health metric.
 * Answers the CEO-level question: "Are users actually getting more
 * interview-ready by using our product?"
 *
 * Visual anchor is the three-number arc: First Score → Latest Score → Δ.
 * If Δ is positive, the delta glows emerald; negative → rose. Four small
 * KPI cards below break down cohort-level improvement quality (% improved,
 * % improving by 5+, % by 10+, avg practices between assessments).
 *
 * Renders even when there's no data yet - an empty state that nudges the
 * owner to seed the first Official assessment.
 */
function ReadinessImpactPanel({ data }) {
  if (!data) return null;
  const {
    total_assessments = 0,
    total_users_scored = 0,
    users_with_2plus = 0,
    avg_first_score,
    avg_latest_score,
    avg_improvement,
    pct_improved = 0,
    pct_improved_5plus = 0,
    pct_improved_10plus = 0,
    avg_practices_between,
    definition,
  } = data;

  const hasData = total_assessments > 0;
  const delta = avg_improvement ?? 0;
  const positive = delta > 0;
  const negative = delta < 0;
  const arrow = positive ? "↑" : negative ? "↓" : "→";
  const deltaColor = positive
    ? "text-emerald-400"
    : negative
    ? "text-rose-400"
    : "text-zinc-400";

  return (
    <section
      data-testid="readiness-impact-panel"
      className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.06] via-zinc-950 to-zinc-950 p-5 shadow-lg shadow-amber-500/[0.03]"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold">Readiness Impact</div>
          <MetricTooltip text={definition || ""} testid="readiness-impact-info" />
        </div>
        <div className="text-[11px] text-zinc-500 tabular-nums">
          {total_assessments} assessment{total_assessments === 1 ? "" : "s"} · {total_users_scored} user{total_users_scored === 1 ? "" : "s"} scored
        </div>
      </div>

      {!hasData ? (
        <div data-testid="readiness-empty" className="py-8 text-center text-sm text-zinc-500">
          No Official Interview assessments completed yet. Once users complete two or more, this panel will show how much they&apos;re improving.
        </div>
      ) : (
        <>
          {/* Three-number arc: First → Latest → Δ. This is the visual the
              owner reads at a glance. Fixed heights keep the row aligned
              even when a value is missing. */}
          <div className="grid grid-cols-3 gap-3 items-center">
            <ArcStat
              testid="readiness-first"
              label="First Score"
              value={avg_first_score}
              suffix="/100"
              tone="neutral"
            />
            <ArcStat
              testid="readiness-latest"
              label="Latest Score"
              value={avg_latest_score}
              suffix="/100"
              tone={positive ? "good" : negative ? "bad" : "neutral"}
              arrow={arrow}
            />
            <ArcStat
              testid="readiness-delta"
              label="Avg Improvement"
              value={avg_improvement}
              suffix=" pts"
              signed
              tone={positive ? "good" : negative ? "bad" : "neutral"}
            />
          </div>

          {/* Cohort quality, only meaningful when users_with_2plus > 0 */}
          {users_with_2plus > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              <MiniStat
                testid="readiness-pct-improved"
                label="% Improved"
                value={`${pct_improved}%`}
                sub={`of ${users_with_2plus} user${users_with_2plus === 1 ? "" : "s"} with 2+ assessments`}
                tooltip="Percentage of users with 2+ assessments whose latest score is strictly higher than their first."
                colorClass={pct_improved >= 50 ? "text-emerald-400" : pct_improved >= 25 ? "text-amber-300" : "text-zinc-100"}
              />
              <MiniStat
                testid="readiness-5plus"
                label="Improved 5+ pts"
                value={`${pct_improved_5plus}%`}
                tooltip="Percentage of 2+ assessment users whose latest score is at least 5 points higher than their first, the 'meaningful' improvement threshold."
                colorClass={pct_improved_5plus >= 30 ? "text-emerald-400" : "text-zinc-100"}
              />
              <MiniStat
                testid="readiness-10plus"
                label="Improved 10+ pts"
                value={`${pct_improved_10plus}%`}
                tooltip="Percentage of 2+ assessment users whose latest score jumped by 10 or more points, the 'strong' improvement threshold."
                colorClass={pct_improved_10plus >= 20 ? "text-emerald-400" : "text-zinc-100"}
              />
              <MiniStat
                testid="readiness-practices"
                label="Avg Practices Between"
                value={avg_practices_between == null ? "–" : avg_practices_between.toFixed(1)}
                sub="mock + live sessions"
                tooltip="Average number of completed mock and live interview sessions each 2+ user completed between their first and latest Official assessment."
              />
            </div>
          ) : (
            <div className="mt-4 text-xs text-zinc-500 italic">
              Improvement metrics unlock once users complete a second Official assessment. Currently {total_users_scored} user{total_users_scored === 1 ? "" : "s"} scored, {users_with_2plus} with 2+ assessments.
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * ArcStat - hero-sized number for the First / Latest / Δ arc. The "arrow"
 * prop drops a small directional indicator above the number to visually
 * connect the three numbers left-to-right.
 */
function ArcStat({ label, value, suffix, arrow, signed, tone = "neutral", testid }) {
  const color = tone === "good" ? "text-emerald-400" : tone === "bad" ? "text-rose-400" : "text-zinc-50";
  const arrowColor = tone === "good" ? "text-emerald-400/70" : tone === "bad" ? "text-rose-400/70" : "text-zinc-500";
  const display =
    value == null
      ? "–"
      : signed && value > 0
      ? `+${Number(value).toFixed(1)}`
      : Number(value).toFixed(1);
  return (
    <div data-testid={testid} className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-4 text-center">
      {arrow && (
        <div className={`text-2xl leading-none mb-1 ${arrowColor}`} aria-hidden="true">{arrow}</div>
      )}
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-3xl font-semibold mt-1 tabular-nums ${color}`}>
        {display}
        {value != null && suffix && <span className="text-sm text-zinc-500 ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

/** MiniStat - small supporting KPI card for the readiness cohort breakdown. */
function MiniStat({ label, value, sub, tooltip, colorClass, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-zinc-800/70 bg-zinc-900/40 p-3">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
        <span>{label}</span>
        {tooltip && <MetricTooltip text={tooltip} testid={`${testid}-info`} />}
      </div>
      <div className={`text-lg font-semibold mt-1 tabular-nums ${colorClass || "text-zinc-100"}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RevenueChart({ series, range }) {
  // SVG line chart w/ real daily revenue. No external chart lib - keeps the
  // bundle lean. When every day is $0, we still render an empty axis so the
  // admin sees "yep, checked, no revenue yet" rather than a blank frame.
  const width = 780, height = 160, padL = 44, padR = 12, padT = 12, padB = 26;
  const iw = width - padL - padR, ih = height - padT - padB;
  const totalCents = series.reduce((s, d) => s + (d.revenue_cents || 0), 0);
  const maxCents = Math.max(1, ...series.map((d) => d.revenue_cents || 0));
  const points = series.map((d, i) => {
    const x = series.length <= 1 ? padL : padL + (i / (series.length - 1)) * iw;
    const y = padT + (1 - (d.revenue_cents || 0) / maxCents) * ih;
    return { x, y, ...d };
  });
  const pathD = points.length
    ? "M " + points.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" L ")
    : "";
  const areaD = points.length
    ? `${pathD} L ${points[points.length - 1].x.toFixed(1)} ${(padT + ih).toFixed(1)} L ${points[0].x.toFixed(1)} ${(padT + ih).toFixed(1)} Z`
    : "";
  // 3 y-axis labels: 0, mid, max.
  const yLabels = [0, Math.round(maxCents / 2), maxCents];
  const fmtY = (c) => (c >= 100_00 ? `$${Math.round(c / 100).toLocaleString()}` : `$${(c / 100).toFixed(2)}`);
  return (
    <section data-testid="revenue-chart" className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400">Daily Revenue</div>
          <div className="text-xs text-zinc-500">Real billing history · newest right · {range === "all" ? "last 30 days" : `last ${range}`}</div>
        </div>
        <div className="text-lg font-semibold text-emerald-300 tabular-nums">{fmtCents(totalCents)}</div>
      </div>
      <div className="w-full overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[520px] h-40" role="img" aria-label="Daily revenue chart">
          {/* gridlines */}
          {yLabels.map((v, i) => {
            const y = padT + (1 - v / maxCents) * ih;
            return (
              <g key={i}>
                <line x1={padL} x2={width - padR} y1={y} y2={y} stroke="rgb(39 39 42)" strokeDasharray="2 3" />
                <text x={padL - 6} y={y + 3} textAnchor="end" className="fill-zinc-500" fontSize="10">{fmtY(v)}</text>
              </g>
            );
          })}
          {/* area + line */}
          {areaD && <path d={areaD} fill="rgba(245,158,11,0.12)" />}
          {pathD && <path d={pathD} fill="none" stroke="#f59e0b" strokeWidth="1.5" />}
          {/* dots on days with revenue */}
          {points.filter((p) => (p.revenue_cents || 0) > 0).map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="2.6" fill="#f59e0b" />
              <title>{`${p.date} · ${fmtCents(p.revenue_cents)} · ${p.purchases} purchase${p.purchases === 1 ? "" : "s"}`}</title>
            </g>
          ))}
          {/* x-axis end labels */}
          {points.length > 0 && (
            <>
              <text x={padL} y={height - 8} className="fill-zinc-500" fontSize="10">{points[0].date}</text>
              <text x={width - padR} y={height - 8} textAnchor="end" className="fill-zinc-500" fontSize="10">{points[points.length - 1].date}</text>
            </>
          )}
        </svg>
      </div>
      {totalCents === 0 && (
        <div className="text-xs text-zinc-500 mt-1 text-center">No revenue in this range yet.</div>
      )}
    </section>
  );
}

function PlanBreakdown({ breakdown }) {
  const totalActive = breakdown.reduce((s, p) => s + (p.active || 0), 0);
  return (
    <section data-testid="plan-breakdown" className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-3">Paid Plan Breakdown</div>
      {breakdown.length === 0 ? (
        <div className="text-xs text-zinc-500 py-4 text-center">No paid plans configured.</div>
      ) : (
        <ul className="space-y-3">
          {breakdown.map((p) => {
            const share = totalActive ? Math.round((p.active / totalActive) * 100) : 0;
            return (
              <li key={p.plan_id} data-testid={`plan-row-${p.plan_id}`} className="space-y-1">
                <div className="flex items-baseline justify-between gap-3 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-100 font-medium truncate">{p.name}</div>
                    <div className="text-[10px] text-zinc-500">
                      {fmtCents(p.price_cents)}
                      {p.billing_interval && p.billing_interval !== "one_time" ? ` / ${p.billing_interval}` : ""}
                    </div>
                  </div>
                  <div className="text-right tabular-nums">
                    <div className="text-zinc-50 text-base font-semibold">{p.active}</div>
                    <div className="text-[10px] text-zinc-500">active{p.cancelled ? ` · ${p.cancelled} cancelled` : ""}</div>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-zinc-900 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-500/40" style={{ width: `${Math.max(2, share)}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {totalActive === 0 && (
        <div className="text-xs text-zinc-500 mt-3 text-center">No active paid subscribers yet.</div>
      )}
    </section>
  );
}

function RefundsLog({ rows, range }) {
  return (
    <section data-testid="refunds-log" className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-400">Refunds & Cancellations</div>
        <div className="text-[10px] text-zinc-500 tabular-nums">{rows.length} in {range === "all" ? "all time" : `last ${range}`}</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-500 py-6 text-center">No refunds or cancellations in this range.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left py-1.5 font-medium">Date</th>
                <th className="text-left py-1.5 font-medium">User</th>
                <th className="text-left py-1.5 font-medium">Plan</th>
                <th className="text-left py-1.5 font-medium">Kind</th>
                <th className="text-right py-1.5 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid={`refund-row-${i}`} className="border-t border-zinc-800/50">
                  <td className="py-1.5 text-zinc-300 tabular-nums">{fmtDate(r.created_at)}</td>
                  <td className="py-1.5">
                    <div className="text-zinc-100">{r.name || "–"}</div>
                    <div className="text-[10px] text-zinc-500 truncate max-w-[220px]">{r.email || r.user_id}</div>
                  </td>
                  <td className="py-1.5 text-zinc-300">{r.plan_id || "–"}</td>
                  <td className="py-1.5">
                    <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${
                      r.kind === "refund"
                        ? "border-rose-500/30 bg-rose-500/[0.06] text-rose-300"
                        : "border-amber-500/30 bg-amber-500/[0.06] text-amber-300"
                    }`}>{r.kind}</span>
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-rose-300">
                    {r.amount_cents ? `-${fmtCents(r.amount_cents)}` : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------- Users list -----------------------------------------------------

function UsersTab({ onSelectUser }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("recent");
  const [plan, setPlan] = useState("all");
  const [active, setActive] = useState("all");
  const [data, setData] = useState({ users: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams({ q, page: String(page), page_size: "25", sort, plan, active });
      const { data } = await api.get(`/admin/analytics/users?${params}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  }, [q, page, sort, plan, active]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, sort, plan, active]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 25)));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            data-testid="users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-8 pr-3 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/40"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <select data-testid="users-plan-filter" value={plan} onChange={(e) => setPlan(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-xs text-zinc-100">
          <option value="all">All plans</option>
          <option value="free">Free</option>
          <option value="interview_ready_pass">Interview Ready Pass</option>
          <option value="career_pro">Career Pro</option>
          <option value="career_enterprise">Career Enterprise</option>
        </select>
        <select data-testid="users-active-filter" value={active} onChange={(e) => setActive(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-xs text-zinc-100">
          <option value="all">Any activity</option>
          <option value="active">Active (30d)</option>
          <option value="inactive">Inactive</option>
        </select>
        <select data-testid="users-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-md px-2 py-1.5 text-xs text-zinc-100">
          <option value="recent">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="last_active">Last active</option>
          <option value="email">Email A–Z</option>
        </select>
      </div>

      {err && <div className="text-sm text-rose-400">{err}</div>}

      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Email</th>
              <th className="text-left px-3 py-2 font-medium">Registered</th>
              <th className="text-left px-3 py-2 font-medium">Last Active</th>
              <th className="text-left px-3 py-2 font-medium">Plan</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">WS</th>
              <th className="text-right px-3 py-2 font-medium">Interviews</th>
              <th className="text-right px-3 py-2 font-medium">Readiness</th>
              <th className="text-right px-3 py-2 font-medium">Lifetime</th>
            </tr>
          </thead>
          <tbody>
            {(data.users || []).map((u) => (
              <tr
                key={u.user_id}
                onClick={() => onSelectUser(u.user_id)}
                data-testid={`users-row-${u.user_id}`}
                className="border-t border-zinc-800/60 hover:bg-zinc-900/60 cursor-pointer transition-colors"
              >
                <td className="px-3 py-2 text-zinc-100">{u.name || "–"}</td>
                <td className="px-3 py-2 text-zinc-300">{u.email}</td>
                <td className="px-3 py-2 text-zinc-400 tabular-nums" title={u.registered_at || ""}>{fmtDate(u.registered_at)}</td>
                <td className="px-3 py-2 text-zinc-400 tabular-nums" title={u.last_active_at || ""}>{ago(u.last_active_at)}</td>
                <td className="px-3 py-2 text-zinc-200">{u.plan_id}</td>
                <td className="px-3 py-2 text-zinc-400">{u.subscription_status || "–"}</td>
                <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{u.workspaces}</td>
                <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{u.interviews_completed}</td>
                <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{u.readiness_score ?? "–"}</td>
                <td className="px-3 py-2 text-right text-emerald-300 tabular-nums">{fmtCents(u.lifetime_revenue_cents)}</td>
              </tr>
            ))}
            {(!loading && (data.users || []).length === 0) && (
              <tr><td colSpan="10" className="px-3 py-8 text-center text-zinc-500">No users match this filter yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span data-testid="users-total">{data.total || 0} users total</span>
        <div className="flex items-center gap-1">
          <button
            disabled={page <= 1}
            onClick={() => setPage((n) => Math.max(1, n - 1))}
            data-testid="users-prev"
            className="h-7 w-7 flex items-center justify-center rounded-full border border-zinc-800 disabled:opacity-40 hover:text-amber-300 hover:border-amber-500/40"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>
          <span className="tabular-nums px-1">Page {page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((n) => n + 1)}
            data-testid="users-next"
            className="h-7 w-7 flex items-center justify-center rounded-full border border-zinc-800 disabled:opacity-40 hover:text-amber-300 hover:border-amber-500/40"
          >
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- User detail ----------------------------------------------------

function UserDetailTab({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(`/admin/analytics/users/${userId}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load user");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [userId]);

  if (loading) return <div className="text-sm text-zinc-500">Loading user…</div>;
  if (err) return <div className="text-sm text-rose-400">{err}</div>;
  if (!data) return null;

  const a = data.account, u = data.usage, p = data.performance, r = data.revenue;

  return (
    <div className="space-y-4">
      <button onClick={onBack} data-testid="user-detail-back" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-amber-300">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to users
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Account</div>
          <div className="text-lg font-semibold text-zinc-50">{a.name || "–"}</div>
          <div className="text-xs text-zinc-400 mb-3 break-all">{a.email}</div>
          <dl className="grid grid-cols-2 gap-y-1.5 text-xs">
            <dt className="text-zinc-500">Registered</dt>
            <dd className="text-zinc-200 text-right tabular-nums">{fmtDate(a.registered_at)}</dd>
            <dt className="text-zinc-500">Last active</dt>
            <dd className="text-zinc-200 text-right tabular-nums">{ago(a.last_active_at)}</dd>
            <dt className="text-zinc-500">Plan</dt>
            <dd className="text-zinc-200 text-right">{a.plan_id}</dd>
            <dt className="text-zinc-500">Status</dt>
            <dd className="text-zinc-200 text-right">{a.subscription_status || "–"}</dd>
            {a.subscription_expires_at && (<>
              <dt className="text-zinc-500">Expires</dt>
              <dd className="text-zinc-200 text-right tabular-nums">{fmtDate(a.subscription_expires_at)}</dd>
            </>)}
          </dl>
        </section>

        <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Usage</div>
          <dl className="grid grid-cols-2 gap-y-1.5 text-xs">
            <dt className="text-zinc-500">Resumes uploaded</dt><dd className="text-zinc-100 text-right tabular-nums">{u.resumes_uploaded}</dd>
            <dt className="text-zinc-500">Jobs analyzed</dt><dd className="text-zinc-100 text-right tabular-nums">{u.jobs_analyzed}</dd>
            <dt className="text-zinc-500">Practice interviews</dt><dd className="text-zinc-100 text-right tabular-nums">{u.mock_interviews}</dd>
            <dt className="text-zinc-500">Live interviews</dt><dd className="text-zinc-100 text-right tabular-nums">{u.live_interviews}</dd>
            <dt className="text-zinc-500">Interview replays</dt><dd className="text-zinc-100 text-right tabular-nums">{u.interview_replays}</dd>
            <dt className="text-zinc-500">Interview briefs</dt><dd className="text-zinc-100 text-right tabular-nums">{u.interview_briefs}</dd>
          </dl>
        </section>

        <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Performance</div>
          <div className="text-2xl font-semibold text-zinc-50 tabular-nums">
            {p.current_readiness ?? "–"}<span className="text-xs text-zinc-500 ml-1">readiness</span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {p.previous_readiness !== null && p.previous_readiness !== undefined
              ? <>Previous: <span className="text-zinc-200 tabular-nums">{p.previous_readiness}</span></>
              : "No previous session yet"}
          </div>
          <div className="mt-2 text-xs text-zinc-500">
            Overall interview score: <span className="text-zinc-200 tabular-nums">{p.current_overall ?? "–"}</span>
          </div>
          <hr className="border-zinc-800 my-3" />
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Revenue</div>
          <div className="text-2xl font-semibold text-emerald-300 tabular-nums">{fmtCents(r.lifetime_cents)}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{r.purchases.length} recorded {r.purchases.length === 1 ? "purchase" : "purchases"}</div>
        </section>
      </div>

      {/* Purchases */}
      {r.purchases.length > 0 && (
        <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Purchases</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-zinc-500 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left py-1.5 font-medium">Date</th>
                  <th className="text-left py-1.5 font-medium">Kind</th>
                  <th className="text-left py-1.5 font-medium">Plan</th>
                  <th className="text-left py-1.5 font-medium">Description</th>
                  <th className="text-right py-1.5 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {r.purchases.map((p, i) => (
                  <tr key={i} className="border-t border-zinc-800/50">
                    <td className="py-1.5 text-zinc-300 tabular-nums">{fmtDateTime(p.created_at)}</td>
                    <td className="py-1.5 text-zinc-400">{p.kind || "–"}</td>
                    <td className="py-1.5 text-zinc-200">{p.plan_id || "–"}</td>
                    <td className="py-1.5 text-zinc-400">{p.description || "–"}</td>
                    <td className="py-1.5 text-right text-emerald-300 tabular-nums">{fmtCents(p.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Timeline */}
      <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-3">Activity Timeline</div>
        {data.timeline.length === 0 ? (
          <div className="text-xs text-zinc-500 py-4 text-center">No activity yet.</div>
        ) : (
          <ul className="space-y-2">
            {data.timeline.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-xs">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-100">{t.label}</div>
                  <div className="text-zinc-500 tabular-nums">{fmtDateTime(t.at)}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">{t.kind}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------- Funnel --------------------------------------------------------

function FunnelTab({ range, setRange }) {
  const [data, setData] = useState(null);
  const [activation, setActivation] = useState(null);
  const [freeToPaid, setFreeToPaid] = useState(null);
  const [guestFunnel, setGuestFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [f, a, fp, gf] = await Promise.all([
        api.get(`/admin/analytics/funnel?range=${range}`),
        api.get(`/admin/analytics/activation?range=${range}`),
        api.get(`/admin/analytics/free_to_paid?range=${range}`),
        api.get(`/admin/analytics/guest-funnel?range=${range}`),
      ]);
      setData(f.data);
      setActivation(a.data);
      setFreeToPaid(fp.data);
      setGuestFunnel(gf.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load funnel");
    } finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-sm text-zinc-500">Loading funnel…</div>;
  if (err) return <div className="text-sm text-rose-400">{err}</div>;
  if (!data) return null;

  const steps = data.steps || [];
  const top = data.total_registered || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <RangeChips range={range} onChange={setRange} />
        <span className="text-xs text-zinc-500">{fmtNum(top)} registered in range</span>
      </div>

      {/* Full journey funnel, reuses existing bar-style rendering */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">
          Full Journey
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-2">
          {steps.map((s, i) => {
            const conv = i === 0 ? 100 : (steps[i - 1].count > 0 ? Math.round((s.count / steps[i - 1].count) * 100) : 0);
            return (
              <div key={s.key} data-testid={`funnel-step-${s.key}`} className="flex items-center gap-3">
                <div className="w-56 flex-shrink-0 text-xs text-zinc-300">{s.label}</div>
                <div className="flex-1 h-8 bg-zinc-900 rounded-md overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500/40 to-amber-500/20"
                    style={{ width: `${Math.min(100, Math.max(2, s.pct_of_top))}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-3 justify-between text-[11px]">
                    <span className="text-zinc-100 font-semibold tabular-nums">{fmtNum(s.count)}</span>
                    <span className="text-zinc-500 tabular-nums">
                      {s.pct_of_top}% of top{i > 0 && <span className="text-zinc-400 ml-2">↳ {conv}% from prev</span>}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {top === 0 && <div className="text-sm text-zinc-500 py-8 text-center">No data yet in this range.</div>}
        </div>
      </div>

      <ActivationSection data={activation} />
      <FreeToPaidSection data={freeToPaid} />
      <GuestFunnelSection data={guestFunnel} />
    </div>
  );
}

function GuestFunnelSection({ data }) {
  if (!data) return null;
  const steps = data.steps || [];
  const top = steps[0]?.count || 0;
  return (
    <div data-testid="guest-funnel-section">
      <div className="text-[11px] uppercase tracking-[0.24em] text-sky-400 mb-2">
        Guest Sample Funnel
      </div>
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-2">
        {steps.map((s, i) => (
          <div key={s.key} data-testid={`guest-funnel-step-${s.key}`} className="flex items-center gap-3">
            <div className="w-56 flex-shrink-0 text-xs text-zinc-300">{s.label}</div>
            <div className="flex-1 h-8 bg-zinc-900 rounded-md overflow-hidden relative">
              <div
                className="h-full bg-gradient-to-r from-sky-500/40 to-sky-500/20"
                style={{ width: `${Math.min(100, Math.max(2, s.pct_of_top || 0))}%` }}
              />
              <div className="absolute inset-0 flex items-center px-3 justify-between text-[11px]">
                <span className="text-zinc-100 font-semibold tabular-nums">{fmtNum(s.count)}</span>
                <span className="text-zinc-500 tabular-nums">
                  {s.pct_of_top}% of top
                  {i > 0 && s.rate_from_prev != null && (
                    <span className="text-zinc-400 ml-2">↳ {s.rate_from_prev}% from prev</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
        {top === 0 && (
          <div className="text-sm text-zinc-500 py-4 text-center">No guest activity yet in this range.</div>
        )}
        <div className="text-[11px] text-zinc-500 pt-1 leading-relaxed">{data.note}</div>
      </div>
    </div>
  );
}

/**
 * ActivationSection - progressive milestones from signup to Readiness Score.
 * The step marked `is_biggest_dropoff` gets a red glow so the owner spots
 * the leaky bucket at a glance.
 */
function ActivationSection({ data }) {
  if (!data) return null;
  const steps = data.steps || [];
  return (
    <div data-testid="activation-section">
      <div className="flex items-center gap-1.5 mb-2">
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400">Activation Milestones</div>
        <MetricTooltip text={data.definition || ""} testid="activation-info" />
      </div>
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-2">
        {steps.map((s, i) => {
          const isBiggest = !!s.is_biggest_dropoff;
          return (
            <div
              key={s.key}
              data-testid={`activation-step-${s.key}`}
              className={`flex items-center gap-3 ${isBiggest ? "" : ""}`}
            >
              <div className="w-56 flex-shrink-0 text-xs text-zinc-300 flex items-center gap-1.5">
                <span className="tabular-nums text-zinc-500 w-4">{i + 1}.</span>
                <span>{s.label}</span>
              </div>
              <div className={`flex-1 h-9 rounded-md overflow-hidden relative border ${
                isBiggest ? "border-rose-500/40 bg-rose-500/[0.03]" : "border-transparent bg-zinc-900"
              }`}>
                <div
                  className={`h-full ${isBiggest ? "bg-gradient-to-r from-rose-500/25 to-rose-500/10" : "bg-gradient-to-r from-emerald-500/25 to-emerald-500/10"}`}
                  style={{ width: `${Math.min(100, Math.max(2, s.pct_of_top))}%` }}
                />
                <div className="absolute inset-0 flex items-center px-3 justify-between text-[11px]">
                  <span className="text-zinc-100 font-semibold tabular-nums">{fmtNum(s.count)}</span>
                  <span className="tabular-nums flex items-center gap-2">
                    <span className="text-zinc-500">{s.pct_of_top}% of signups</span>
                    {i > 0 && (
                      <span className={`inline-flex items-center gap-0.5 ${
                        s.drop_pct >= 50 ? "text-rose-400" : s.drop_pct >= 25 ? "text-amber-400" : "text-emerald-400"
                      }`}>
                        <ArrowDownRight className="h-3 w-3" />{s.drop_pct}% drop
                      </span>
                    )}
                    {isBiggest && (
                      <span className="text-[10px] uppercase tracking-widest text-rose-400 font-semibold">biggest drop</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
        {(data.total_signed_up || 0) === 0 && (
          <div className="text-sm text-zinc-500 py-8 text-center">No signups yet in this range.</div>
        )}
      </div>
    </div>
  );
}

/**
 * FreeToPaidSection - for each product behaviour, shows how many users did
 * the behaviour vs. how many of those users became paid. Rows are sorted
 * high-to-low so the strongest paid predictor floats to the top and the
 * owner immediately sees which flow to double down on.
 */
function FreeToPaidSection({ data }) {
  if (!data) return null;
  const behaviours = [...(data.behaviours || [])].sort((a, b) => (b.conversion_rate || 0) - (a.conversion_rate || 0));
  return (
    <div data-testid="free-to-paid-section">
      <div className="flex items-center gap-1.5 mb-2">
        <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400">Free → Paid Conversion</div>
        <MetricTooltip text={data.definition || ""} testid="free-to-paid-info" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <KpiCard testid="ftp-total-users" label="Total Users" value={fmtNum(data.total_users)} />
        <KpiCard testid="ftp-free" label="Free Users" value={fmtNum(data.total_free)} />
        <KpiCard testid="ftp-paid" label="Paid Users" value={fmtNum(data.total_paid)} />
        <KpiCard
          testid="ftp-rate"
          label="Overall Conversion"
          value={`${data.overall_conversion_rate || 0}%`}
          tooltip="Percentage of users in this range who became paid customers, regardless of which behaviour they performed."
        />
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <div className="grid grid-cols-[minmax(0,1fr)_100px_100px_100px] gap-2 px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800/60">
          <div>Behaviour</div>
          <div className="text-right">Users</div>
          <div className="text-right">Converted</div>
          <div className="text-right">Conv. Rate</div>
        </div>
        {behaviours.map((b) => {
          const isTop = b === behaviours[0] && b.conversion_rate > 0;
          return (
            <div
              key={b.key}
              data-testid={`ftp-row-${b.key}`}
              className={`grid grid-cols-[minmax(0,1fr)_100px_100px_100px] gap-2 px-4 py-2.5 text-xs border-b border-zinc-800/40 last:border-b-0 ${
                isTop ? "bg-emerald-500/[0.04]" : ""
              }`}
            >
              <div className="text-zinc-200 flex items-center gap-2">
                {b.label}
                {isTop && <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-semibold">top predictor</span>}
              </div>
              <div className="text-right tabular-nums text-zinc-300">{fmtNum(b.users)}</div>
              <div className="text-right tabular-nums text-zinc-300">{fmtNum(b.converted)}</div>
              <div className={`text-right tabular-nums font-semibold ${
                b.conversion_rate >= 30 ? "text-emerald-400" : b.conversion_rate >= 10 ? "text-amber-300" : "text-zinc-400"
              }`}>{b.conversion_rate}%</div>
            </div>
          );
        })}
        {behaviours.length === 0 && (
          <div className="text-sm text-zinc-500 py-8 text-center">No behaviour data yet in this range.</div>
        )}
      </div>
    </div>
  );
}

// ---------- Product Tab ---------------------------------------------------

/**
 * ProductTab - feature-usage KPIs grouped by Intuiterview product surface.
 * Each block is a `FeatureBlock` with the feature name, a short subtitle,
 * and 3–5 KPI cards. Metrics that require dedicated event tracking (e.g.
 * replay opens) are surfaced as "Not yet tracked" instead of showing a
 * misleading 0.
 */
function ProductTab({ range, setRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/admin/analytics/product?range=${range}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load product analytics");
    } finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-sm text-zinc-500">Loading product analytics…</div>;
  if (err) return <div className="text-sm text-rose-400">{err}</div>;
  if (!data) return null;

  const r = data.resume || {};
  const p = data.prediction || {};
  const m = data.mock || {};
  const l = data.live || {};
  const rp = data.replay || {};
  const o = data.official || {};

  return (
    <div className="space-y-5" data-testid="product-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RangeChips range={range} onChange={setRange} />
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <MetricTooltip text={data.definition || ""} testid="product-def-info" />
          <span>Feature usage from real production records</span>
          <button
            onClick={load}
            data-testid="product-refresh"
            className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-amber-300 ml-2"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <FeatureBlock
        title="Resume Alignment"
        subtitle="Uploads · JDs · Analyses · Alignment & ATS scores"
        testid="feature-resume"
        cards={[
          { label: "Resumes Uploaded", value: fmtNum(r.resumes_uploaded), testid: "resume-uploaded",
            tooltip: "Distinct resumes uploaded and parsed into the platform." },
          { label: "Job Descriptions Added", value: fmtNum(r.jds_added), testid: "resume-jds",
            tooltip: "Analyses that carry a non-empty job description." },
          { label: "Analyses Completed", value: fmtNum(r.analyses_completed), testid: "resume-analyses",
            tooltip: "Analyses where the LLM scoring pipeline successfully produced a match score." },
          { label: "Avg Alignment Score", value: pctVal(r.avg_alignment_score), testid: "resume-avg-align",
            tooltip: "Mean overall match_score across every completed analysis." },
          { label: "Avg ATS Score", value: pctVal(r.avg_ats_score), testid: "resume-avg-ats",
            tooltip: "Mean ATS-friendliness score across every completed analysis." },
        ]}
      />

      <FeatureBlock
        title="Interview Prediction"
        subtitle="Predicted-questions generation & practice engagement"
        testid="feature-prediction"
        cards={[
          { label: "Users Generated", value: fmtNum(p.users_generated), testid: "predict-users",
            tooltip: "Distinct users who created at least one predicted-question set." },
          { label: "Total Questions Generated", value: fmtNum(p.total_questions_generated), testid: "predict-total-qs",
            tooltip: "Sum of question counts across every generated set." },
          { label: "Avg Questions Practiced", value: (p.avg_questions_practiced ?? 0).toFixed(1), testid: "predict-avg-practiced",
            tooltip: "Mean number of questions with a user-submitted answer per generated set." },
        ]}
      />

      <FeatureBlock
        title="AI Mock Interview"
        subtitle="Practice sessions started, completed, and depth of engagement"
        testid="feature-mock"
        cards={[
          { label: "Started", value: fmtNum(m.started), testid: "mock-started",
            tooltip: "Mock sessions ever initiated by a user (any status)." },
          { label: "Completed", value: fmtNum(m.completed), testid: "mock-completed",
            tooltip: "Mock sessions with status == completed." },
          { label: "Completion Rate", value: `${m.completion_rate ?? 0}%`, testid: "mock-rate",
            tooltip: "Completed / Started × 100." },
          { label: "Avg Questions Answered", value: m.avg_questions_answered == null ? "–" : m.avg_questions_answered.toFixed(1), testid: "mock-avg-ans",
            tooltip: "Mean user messages per completed mock session (proxy for question depth)." },
        ]}
      />

      <FeatureBlock
        title="Live AI Interview"
        subtitle="Video interviews with the AI panel"
        testid="feature-live"
        cards={[
          { label: "Started", value: fmtNum(l.started), testid: "live-started",
            tooltip: "Video sessions ever initiated (any status)." },
          { label: "Completed", value: fmtNum(l.completed), testid: "live-completed",
            tooltip: "Video sessions with status == completed." },
          { label: "Completion Rate", value: `${l.completion_rate ?? 0}%`, testid: "live-rate",
            tooltip: "Completed / Started × 100." },
          { label: "Avg Duration", value: l.avg_minutes_est == null ? "–" : `${l.avg_minutes_est} min`, testid: "live-duration",
            tooltip: "Estimated using average turns × 90s. Wire true timing signal to make this exact." },
          { label: "Sample Tours", value: fmtNum(l.sample_tours), testid: "live-sample-tours",
            tooltip: "Sample/demo interviews (is_sample=true). Excluded from every genuine-customer metric above." },
        ]}
      />

      <FeatureBlock
        title="Interview Replay"
        subtitle="Re-watching interviews & reviewing feedback"
        testid="feature-replay"
        cards={[
          { label: "Replays Available", value: fmtNum(rp.replays_available), testid: "replay-available",
            tooltip: "Completed video sessions that have a scored report and can be replayed." },
          { label: "Replays Opened", value: rp.replays_opened == null ? "Not yet tracked" : fmtNum(rp.replays_opened), testid: "replay-opened",
            tooltip: "Distinct users who opened at least one interview report/replay in this range. Fired client-side on ReportPanel mount.",
            dim: rp.replays_opened == null },
          { label: "Feedback Viewed", value: rp.feedback_viewed == null ? "Not yet tracked" : fmtNum(rp.feedback_viewed), testid: "replay-feedback",
            tooltip: "Distinct users who actually saw AI coaching feedback (enrichment ready) in this range. Fires when the enrichment pipeline completes and the user is viewing the report.",
            dim: rp.feedback_viewed == null },
        ]}
      />

      <FeatureBlock
        title="Official Interview Readiness"
        subtitle="Formal readiness assessments & score improvement"
        testid="feature-official"
        cards={[
          { label: "Assessments Completed", value: fmtNum(o.assessments_completed), testid: "official-completed",
            tooltip: "Total interview_reports on file (each represents one official readiness assessment)." },
          { label: "Avg First Score", value: pctVal(o.avg_readiness_first), testid: "official-avg-first",
            tooltip: "Mean readiness_score across every user's FIRST assessment." },
          { label: "Avg Latest Score", value: pctVal(o.avg_readiness_latest), testid: "official-avg-latest",
            tooltip: "Mean readiness_score across every user's LATEST assessment." },
          { label: "Users with 2+ Assessments", value: fmtNum(o.users_with_2plus), testid: "official-repeat",
            tooltip: "Distinct users who completed at least two readiness assessments." },
          {
            label: "Avg Improvement",
            value: o.avg_improvement == null ? "–" : `${o.avg_improvement >= 0 ? "+" : ""}${o.avg_improvement.toFixed(1)}`,
            testid: "official-improvement",
            tooltip: "Latest − First across users with 2+ assessments. Positive = users are getting better.",
            good: o.avg_improvement != null && o.avg_improvement >= 0,
            bad: o.avg_improvement != null && o.avg_improvement < 0,
          },
        ]}
      />
    </div>
  );
}

function pctVal(v) { return v == null ? "–" : `${v.toFixed(1)}`; }

/**
 * FeatureBlock - one bordered card per Intuiterview feature, containing a
 * responsive grid of KPI mini-cards. Left border in gold to reinforce the
 * dashboard's accent language.
 */
function FeatureBlock({ title, subtitle, cards, testid }) {
  return (
    <section
      data-testid={testid}
      className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 border-l-2 border-l-amber-500/60"
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100 tracking-tight">{title}</div>
          {subtitle && <div className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div
            key={c.testid}
            data-testid={c.testid}
            className={`rounded-lg border border-zinc-800/70 bg-zinc-900/40 p-3 ${
              c.dim ? "opacity-70" : ""
            }`}
          >
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
              <span>{c.label}</span>
              {c.tooltip && <MetricTooltip text={c.tooltip} testid={`${c.testid}-info`} />}
            </div>
            <div className={`text-lg font-semibold mt-1 tabular-nums ${
              c.good ? "text-emerald-400" : c.bad ? "text-rose-400" : "text-zinc-50"
            }`}>{c.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}


// ---------- Feedback tab ----------------------------------------------------

const FB_CATEGORY_LABELS = {
  idea: "💡 Idea",
  bug: "🐛 Bug",
  confusing: "😕 Confusing",
  slow: "⚡ Slow",
  love: "❤️ Love",
  other: "💬 Other",
  contextual_interview_helpful: "Interview helpful?",
  contextual_report_helpful: "Report helpful?",
  contextual_slow_wait: "Wait acceptable?",
  deliverable_rating: "Deliverable rating",
};

const DELIVERABLE_LABELS = {
  resume_alignment: "Resume Alignment",
  cover_letter: "Cover Letter",
  predict_questions: "Predict Questions",
  prepare_answers: "Prepare Answers",
  practice_interview: "Practice Interview",
  official_interview: "AI Official Interview",
  interview_brief: "Interview Brief",
};

const RatingStars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5 whitespace-nowrap" title={`${value}/5`}>
    {[1, 2, 3, 4, 5].map((v) => (
      <span key={v} className={v <= value ? "text-amber-300" : "text-zinc-700"}>★</span>
    ))}
  </span>
);

function FeedbackTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deliverableFilter, setDeliverableFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/admin/feedback");
        setData(d);
      } catch {
        setError("Failed to load feedback.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="py-16 text-center text-zinc-500 text-sm">Loading feedback…</div>;
  if (error) return <div className="py-16 text-center text-rose-300 text-sm">{error}</div>;

  const items = data?.items || [];
  const counts = data?.counts || {};
  const ratings = items.filter((f) => f.category === "deliverable_rating");
  const others = items.filter((f) => f.category !== "deliverable_rating");
  const byFeature = {};
  ratings.forEach((f) => {
    const key = f.context?.deliverable || "other";
    (byFeature[key] = byFeature[key] || []).push(f);
  });
  const featureKeys = Object.keys(DELIVERABLE_LABELS).filter((k) => byFeature[k]);
  const avgOf = (list) => (list.length ? (list.reduce((s, f) => s + (f.rating || 0), 0) / list.length).toFixed(1) : "–");
  const visibleRatings = deliverableFilter === "all" ? ratings : (byFeature[deliverableFilter] || []);

  return (
    <div className="space-y-6" data-testid="admin-feedback-tab">
      {/* Micro-feedback: per-deliverable ratings */}
      <div className="space-y-3" data-testid="admin-deliverable-feedback">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Deliverable ratings</div>
        {ratings.length === 0 ? (
          <div className="text-sm text-zinc-500">No deliverable ratings yet.</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="admin-fb-filter-all"
                onClick={() => setDeliverableFilter("all")}
                className={`text-[11px] rounded-full border px-2.5 py-1 transition-colors ${
                  deliverableFilter === "all"
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                    : "border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:border-zinc-700"
                }`}
              >
                All · <span className="tabular-nums font-semibold">{avgOf(ratings)}★</span> ({ratings.length})
              </button>
              {featureKeys.map((k) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`admin-fb-filter-${k}`}
                  onClick={() => setDeliverableFilter(k)}
                  className={`text-[11px] rounded-full border px-2.5 py-1 transition-colors ${
                    deliverableFilter === k
                      ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                      : "border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:border-zinc-700"
                  }`}
                >
                  {DELIVERABLE_LABELS[k]} · <span className="tabular-nums font-semibold">{avgOf(byFeature[k])}★</span> ({byFeature[k].length})
                </button>
              ))}
            </div>
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-deliverable-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/60">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-left px-3 py-2">Feature</th>
                    <th className="text-left px-3 py-2">Rating</th>
                    <th className="text-left px-3 py-2">Comment</th>
                    <th className="text-left px-3 py-2">Testimonial OK</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRatings.map((f) => (
                    <tr key={f.feedback_id} className="border-t border-zinc-800/70 align-top">
                      <td className="px-3 py-2 text-zinc-400 tabular-nums whitespace-nowrap">{fmtDate(f.created_at)}</td>
                      <td className="px-3 py-2 text-zinc-300 max-w-[160px] truncate">{f.email || f.user_id}</td>
                      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">
                        {DELIVERABLE_LABELS[f.context?.deliverable] || f.context?.deliverable || "–"}
                      </td>
                      <td className="px-3 py-2"><RatingStars value={f.rating || 0} /></td>
                      <td className="px-3 py-2 text-zinc-100 max-w-[380px]">{f.text || "–"}</td>
                      <td className="px-3 py-2">
                        {f.testimonial_ok ? (
                          <span className="text-emerald-400 font-semibold">Yes</span>
                        ) : (
                          <span className="text-zinc-600">–</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* General feedback (existing behavior) */}
      <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">General feedback</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(counts).filter(([cat]) => cat !== "deliverable_rating").map(([cat, n]) => (
            <span key={cat} className="text-[11px] rounded-full border border-zinc-800 bg-zinc-900/50 text-zinc-300 px-2.5 py-1">
              {FB_CATEGORY_LABELS[cat] || cat} · <span className="tabular-nums text-zinc-100 font-semibold">{n}</span>
            </span>
          ))}
          {others.length === 0 && (
            <div className="text-sm text-zinc-500">No feedback submitted yet.</div>
          )}
        </div>
        {others.length > 0 && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900/60">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Feedback</th>
                  <th className="text-left px-3 py-2">Page</th>
                </tr>
              </thead>
              <tbody>
                {others.map((f) => (
                  <tr key={f.feedback_id} className="border-t border-zinc-800/70 align-top">
                    <td className="px-3 py-2 text-zinc-400 tabular-nums whitespace-nowrap">{fmtDate(f.created_at)}</td>
                    <td className="px-3 py-2 text-zinc-300 max-w-[160px] truncate">{f.email || f.user_id}</td>
                    <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{FB_CATEGORY_LABELS[f.category] || f.category}</td>
                    <td className="px-3 py-2 text-zinc-100 max-w-[420px]">{f.text || "–"}</td>
                    <td className="px-3 py-2 text-zinc-500 max-w-[160px] truncate">{f.page || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Page wrapper ---------------------------------------------------

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [usersKey, setUsersKey] = useState(0);
  const [range, setRange] = useState("30d");
  const [detailUserId, setDetailUserId] = useState(null);

  // Frontend gate - the backend enforces this too; this is a UX polish.
  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/app/dashboard" replace />;

  const handleSelect = (uid) => { setDetailUserId(uid); setTab("user"); };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="admin-analytics">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1 inline-flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> Owner Analytics
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-zinc-50">Business Dashboard</h1>
          <EnvironmentBadge />
        </div>
        <div className="flex items-center gap-1.5">
          <TabButton id="overview" label="Overview" active={tab === "overview"} onClick={() => setTab("overview")} />
          <TabButton id="users" label="Users" active={tab === "users"} onClick={() => setTab("users")} />
          <TabButton id="funnel" label="Funnel" active={tab === "funnel"} onClick={() => setTab("funnel")} />
          <TabButton id="product" label="Product" active={tab === "product"} onClick={() => setTab("product")} />
          <TabButton id="feedback" label="Feedback" active={tab === "feedback"} onClick={() => setTab("feedback")} />
          {detailUserId && (
            <TabButton id="user" label="User" active={tab === "user"} onClick={() => setTab("user")} />
          )}
        </div>
      </div>

      {tab === "overview" && <OverviewTab range={range} setRange={setRange} />}
      {tab === "users" && (
        <div className="space-y-4">
          <UsersTab key={usersKey} onSelectUser={handleSelect} />
          <DataHygienePanel onPurged={() => setUsersKey((k) => k + 1)} />
        </div>
      )}
      {tab === "user" && detailUserId && (
        <UserDetailTab userId={detailUserId} onBack={() => { setTab("users"); }} />
      )}
      {tab === "funnel" && <FunnelTab range={range} setRange={setRange} />}
      {tab === "product" && <ProductTab range={range} setRange={setRange} />}
      {tab === "feedback" && <FeedbackTab />}
    </div>
  );
}

// Silence unused-imports for icons kept intentionally for future subtabs
void Users; void DollarSign; void TrendingUp;
